import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import type { RegisterReviewerInput } from '@/types/api';

interface AddReviewerDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

type ReviewerRole = RegisterReviewerInput['role'];

const ROLE_OPTIONS: { value: ReviewerRole; label: string }[] = [
  { value: 'cco', label: 'Chief Compliance Officer' },
  { value: 'compliance_rep', label: 'Compliance Representative' },
  { value: 'department_rep', label: 'Department Representative' },
];

export function AddReviewerDialog({ open, onOpenChange }: AddReviewerDialogProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ReviewerRole>('department_rep');
  const [password, setPassword] = useState('');
  const [selectedDepts, setSelectedDepts] = useState<string[]>([]);

  const { data: tenant } = useQuery({
    queryKey: ['tenant'],
    queryFn: () => api.admin.tenant.get(),
    staleTime: 300_000,
  });

  const availableDepts = tenant?.activeDepartments ?? [];

  const createMutation = useMutation({
    mutationFn: (input: RegisterReviewerInput) => api.admin.reviewers.create(input),
    onSuccess: () => {
      toast.success('Reviewer added successfully.');
      queryClient.invalidateQueries({ queryKey: ['reviewers'] });
      handleClose(false);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to add reviewer.');
    },
  });

  const reset = () => {
    setName('');
    setEmail('');
    setRole('department_rep');
    setPassword('');
    setSelectedDepts([]);
  };

  const handleClose = (v: boolean) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const toggleDept = (dept: string) => {
    setSelectedDepts((prev) =>
      prev.includes(dept) ? prev.filter((d) => d !== dept) : [...prev, dept],
    );
  };

  const isValid =
    name.trim().length > 0 &&
    email.trim().length > 0 &&
    password.length >= 8 &&
    selectedDepts.length > 0;

  const handleSubmit = () => {
    createMutation.mutate({
      name: name.trim(),
      email: email.trim(),
      role,
      departments: selectedDepts,
      password,
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Reviewer</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <Label htmlFor="rev-name">Full Name</Label>
            <Input
              id="rev-name"
              placeholder="e.g. Jane Smith"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rev-email">Email</Label>
            <Input
              id="rev-email"
              type="email"
              placeholder="jane@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rev-role">Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as ReviewerRole)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Departments</Label>
            {availableDepts.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {availableDepts.map((dept) => (
                  <label
                    key={dept}
                    className="flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm transition-colors hover:bg-muted/30"
                  >
                    <Checkbox
                      checked={selectedDepts.includes(dept)}
                      onCheckedChange={() => toggleDept(dept)}
                    />
                    <span className="capitalize text-foreground">
                      {dept.replace(/_/g, ' ')}
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                No departments available. Configure departments first.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rev-password">Temporary Password</Label>
            <Input
              id="rev-password"
              type="password"
              placeholder="Min 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {password.length > 0 && password.length < 8 && (
              <p className="text-xs text-red-400">
                Password must be at least 8 characters.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button
            className="bg-teal text-white hover:bg-teal/90"
            disabled={!isValid || createMutation.isPending}
            onClick={handleSubmit}
          >
            {createMutation.isPending && (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            )}
            Add Reviewer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
